import React from 'react';
import Article, { ArticleHeader, ArticleContent, Markdown, Math, ArticleTableOfContents, ArticleFooter, ArticleDisplayAd, ArticleSectionHeader } from '@/components/Article';
import useActiveSection from '@/common/components/Article/useActiveSection';
import { Layout, MainLayout, SectionLayout, SidebarLayout, TableOfContentsLayout } from '@/common/components/layouts/Article';
import settings from './settings';
import { titleize } from '@/common/utils/strings';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SECTIONS = settings.articleSections || [];

const PageArticle = _ref => {
  let {
    updatedAt,
    createdAt,
    authors,
    reviewers = [],
    children
  } = _ref;
  const {
    activeSection,
    onSectionSelect,
    onSectionVisibility
  } = // eslint-disable-next-line react-hooks/rules-of-hooks
  useActiveSection({
    sections: SECTIONS
  });
  return /*#__PURE__*/_jsxs(Layout, {
    hasTableOfContents: true,
    children: [/*#__PURE__*/_jsx(TableOfContentsLayout, {
      hasTopPadding: true,
      children: /*#__PURE__*/_jsx(ArticleTableOfContents, {
        items: SECTIONS,
        activeItemId: activeSection ? activeSection.id : '',
        onActivate: onSectionSelect
      })
    }), /*#__PURE__*/_jsx(MainLayout, {
      children: /*#__PURE__*/_jsxs(SectionLayout, {
        children: [/*#__PURE__*/_jsx(ArticleHeader, {
          heading: 'How You Can Accomplish Your Running Goals',
          subHeading: 'Using a Treadmill Pace Calculator',
          reviewer: reviewers === null || reviewers === void 0 ? void 0 : reviewers[0],
          breadcrumbs: [{
            href: `/${settings.directory}`,
            text: titleize(settings.directory)
          }, {
            text: 'Fitness'
          }],
          authors: authors || [],
          updatedAt: updatedAt,
          createdAt: createdAt
        }), /*#__PURE__*/_jsxs(Article, {
          children: [/*#__PURE__*/_jsx(ArticleContent, {
            children: /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsxs(Markdown, {
                children: [/*#__PURE__*/_jsx(_Fragment, {
                  children: "Running on a treadmill is a great way to reduce the strain on your joints, improve cardiovascular health, and train for running competitions. With some planning, you drastically improve your chances of crushing your next milestone."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "**The treadmill pace calculator is designed to help you find the ideal pace for your next workout.  By using the pace calculation mode, you\u2019ll apply the critical formula:**"
                })]
              }), /*#__PURE__*/_jsx(Math, {
                children: "Pace = Time / Distance"
              }), /*#__PURE__*/_jsx("small", {
                children: "While the calculator will reveal much more information, this essential formula is applied throughout the results.  Pace equals the time to complete your run divided by the distance you reached."
              }), /*#__PURE__*/_jsx(Markdown, {
                children: /*#__PURE__*/_jsx(_Fragment, {
                  children: "Using the other calculator modes, time and distance, you can reverse engineer your running goals in exciting ways.  Let\u2019s look at how you can do just that."
                })
              }), /*#__PURE__*/_jsx(ArticleSectionHeader, {
                elementId: SECTIONS[0].id,
                onToggleVisible: onSectionVisibility,
                children: SECTIONS[0].name
              }), /*#__PURE__*/_jsxs(Markdown, {
                children: [/*#__PURE__*/_jsx(_Fragment, {
                  children: "Each calculator mode will help you determine your run's average treadmill speed setting.  You can find that treadmill speed next to the pace at the top of your results.  Speed is calculated the same way as pace, measured as miles per hour or kilometers per hour."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "**The critical point to understanding treadmill speeds is that they are measured in hourly values.** Pace is functionally the same but measured as the time to reach a mile or kilometer. So, if a treadmill speed displays a value of 3.0, the treadmill belt is moving at a pace of 3 MPH (or 3 KM/H for metric machines)."
                })]
              }), /*#__PURE__*/_jsx(ArticleSectionHeader, {
                elementId: SECTIONS[1].id,
                onToggleVisible: onSectionVisibility,
                children: SECTIONS[1].name
              }), /*#__PURE__*/_jsx(Markdown, {
                children: /*#__PURE__*/_jsx(_Fragment, {
                  children: "By default, your pace will be calculated. However, if you select the \u201CTime\u201D tab, you\u2019ll begin calculating for run time. This mode can be helpful for checking to see if your planned speed will result in a personal record. It uses the following formula to solve for time:"
                })
              }), /*#__PURE__*/_jsx(Math, {
                children: "Time = Distance / Speed"
              }), /*#__PURE__*/_jsx("small", {
                children: "So, time equals the distance of your run divided by the speed or pace.  So if you ran 10 miles at a speed of 10 MPH, you\u2019d have been running for an hour.  Now that we\u2019ve covered time let\u2019s look at the distance calculator."
              }), /*#__PURE__*/_jsx(ArticleSectionHeader, {
                elementId: SECTIONS[2].id,
                onToggleVisible: onSectionVisibility,
                children: SECTIONS[2].name
              }), /*#__PURE__*/_jsx(Markdown, {
                children: /*#__PURE__*/_jsx(_Fragment, {
                  children: "Switching the top tab to \u201CDistance\u201D activates the distance calculator. This mode is a favorite for runners doing [Coopter Testing](https://www.verywellfit.com/fitness-test-for-endurance-12-minute-run-3120264), where you already know the time as well as your target pace. Here, you\u2019ll apply the distance formula:"
                })
              }), /*#__PURE__*/_jsx(Math, {
                children: "Distance = Speed  x  Time"
              }), /*#__PURE__*/_jsx("small", {
                children: "So, if you did that Cooper Test running at a speed of 9 MPH for a period of 12 minutes, you\u2019d reach a formidable distance of 1.8 miles. Now that we\u2019ve covered all the calculator modes let\u2019s look at how to use the incline feature to see comparable paces."
              }), /*#__PURE__*/_jsx(ArticleSectionHeader, {
                elementId: SECTIONS[3].id,
                onToggleVisible: onSectionVisibility,
                children: SECTIONS[3].name
              }), /*#__PURE__*/_jsxs(Markdown, {
                children: [/*#__PURE__*/_jsx(_Fragment, {
                  children: "One of the significant differences between running outdoors and on a treadmill is the incline, as uneven ground surfaces and wind speeds can negatively affect your pace."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "If you have been running on a flat treadmill, consider adding an incline to replicate the feeling of running outdoors. As a general rule of thumb, [increasing the incline by 1 - 2% makes it significantly closer to running outside](https://www.codybeals.com/2014/02/running-numbers-how-much-easier-are/) due to wind resistance and uneven ground."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "That said, inline has more of an effect at slower treadmill speeds than higher speeds. Going from a 0% to 2% incline at a speed of 5 MPH will change the adjusted pace per mile from 00:12:00 to 00:11:05. Simply put, raising the incline to 2% is like running nearly a minute faster per mile on a flat surface.  **At slower speeds, more inline means you\u2019ll exert more energy.**"
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "Adding some incline to your treadmill runs is an excellent way to challenge yourself and create a more natural outdoor feeling. Determine what incline setting you want to run at, and then use the treadmill incline conversion calculator to find the adjusted pace."
                })]
              }), /*#__PURE__*/_jsx(ArticleSectionHeader, {
                elementId: SECTIONS[4].id,
                onToggleVisible: onSectionVisibility,
                children: SECTIONS[4].name
              }), /*#__PURE__*/_jsxs(Markdown, {
                children: [/*#__PURE__*/_jsx(_Fragment, {
                  children: "You can help achieve your running goals using all these calculators to determine the targets you need to achieve. Whether you are just starting or training for a competition, use this helpful app to map out your route to unlocking your next fitness level."
                }), /*#__PURE__*/_jsx(_Fragment, {
                  children: "Furthermore, don\u2019t forget to factor in the time it takes for the treadmill belt to speed up when planning your next run. Then, target the recommended pace, speed, time, distance, or incline as an average toward achieving your next goal."
                })]
              }), children]
            })
          }), /*#__PURE__*/_jsx(ArticleFooter, {
            authors: authors,
            citations: [{
              url: 'https://thirdspacelearning.com/gcse-maths/ratio-and-proportion/speed-distance-time-triangle/',
              text: 'Speed Distance Time Triangle'
            }, {
              url: 'https://www.verywellfit.com/fitness-test-for-endurance-12-minute-run-3120264',
              text: 'Using the Cooper Test 12-Minute Run to Check Aerobic Fitness'
            }, {
              url: 'https://www.codybeals.com/2014/02/running-numbers-how-much-easier-are/',
              text: 'Running The Numbers: How Much Easier Are Treadmills?'
            }, {
              url: 'https://www.hillrunner.com/calculators/treadmill-pace-conversions/',
              text: 'Treadmill Pace Conversions'
            }]
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(SidebarLayout, {
      isMobileCollapsed: true,
      hasTopPadding: true,
      children: /*#__PURE__*/_jsx(ArticleDisplayAd, {
        isSidebar: true
      })
    })]
  });
};

export default PageArticle;